import React, { useCallback, useEffect, useState } from "react";

import { isValid } from "date-fns";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import {
  IFilterComponentProps,
  IFilterInstance,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";

import TimePicker from "@app/component/input/TimePicker";
import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";
import { formatDate } from "@app/util/formatters";
import { parseTime } from "@app/util/parsers";
import keepValidDateOrReturnUndefined from "@app/util/parsers/keepValidDateOrReturnUndefined";

export const TIME_FILTER_OPERATORS = [
  FilterOperator.ON,
  FilterOperator.NOT_ON,
  FilterOperator.AFTER,
  FilterOperator.ON_OR_AFTER,
  FilterOperator.BEFORE,
  FilterOperator.ON_OR_BEFORE,
];

interface TimeFilterProps extends IFilterComponentProps {}

const TimeFilter: IFilterInstance = {
  doesSupportValue: (value: any, operator: FilterOperator) => {
    const date = typeof value === "string" ? parseTime(value) : undefined;
    return isValid(date) && TIME_FILTER_OPERATORS.includes(operator);
  },
  getComponent:
    (): React.FunctionComponent<TimeFilterProps> =>
    ({ className, disabled, focus, onChange, placeholder, value }) => {
      const [date, setDate] = useState(
        value?.value ? keepValidDateOrReturnUndefined(parseTime(value.value)) : undefined
      );

      useEffect(() => {
        setDate((date) => {
          const newDate = value?.value
            ? keepValidDateOrReturnUndefined(parseTime(value.value))
            : undefined;
          return newDate?.valueOf() !== date?.valueOf() ? newDate : date;
        });
      }, [value?.value]);

      // using useEffect instead of "useCallback" for handleTimeChange because we also
      // need to listen to operator changes and transform date accordingly
      useEffect(() => {
        const operator = value.operator;

        onChange?.({
          operator,
          value: date ? formatDate(date, "HH:mm") : undefined,
        });
      }, [date, onChange, value.operator]);

      const setRef = useCallback(
        (ref: { focus: () => void } | null) => {
          if (ref && focus) {
            ref.focus();
          }
        },
        [focus]
      );

      return (
        <TimePicker
          allowClear
          className={className}
          disabled={disabled}
          format={SHOULD_USE_12_HOUR_FORMAT ? "hh:mm a" : "HH:mm"}
          onChange={(date: Date | null) => setDate(date || undefined)}
          placeholder={placeholder as string}
          ref={setRef}
          use12Hours={SHOULD_USE_12_HOUR_FORMAT}
          value={date}
        />
      );
    },
};

export default TimeFilter;
